import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Sidebar from './Sidebar';
import TopNavbar from '../top-navbar/TopNav';
import '../../styles/employee/education-check.css';

const EducationInfo = ({ candidateId, educationInfos, onBack }) => {
    const [educationEntries, setEducationEntries] = useState([]);
    const [educationProofFiles, setEducationProofFiles] = useState({}); // index -> File
    const [errors, setErrors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [overallVerificationStatus, setOverallVerificationStatus] = useState(false);
    const [overallRemarks, setOverallRemarks] = useState('');

    const token = localStorage.getItem('bgc_token');

    const basename = (path) => {
        if (!path) return '';
        try {
            const u = new URL(path);
            return u.pathname.split('/').pop();
        } catch {
            return (path || '').split('/').pop();
        }
    };

    useEffect(() => {
        const init = async () => {
            setLoading(true);
            setError(null);
            try {
                if (!candidateId) {
                    setError('Candidate ID is missing.');
                    setLoading(false);
                    return;
                }

                let backendData = [];
                if (token) {
                    const resp = await fetch(`https://trueface.co.in/api/employee/education/get-education-info/${candidateId}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    if (resp.ok) {
                        const result = await resp.json();
                        backendData = result?.data || [];
                    }
                }

                const mapped = (backendData.length > 0 ? backendData : educationInfos).map((e) => ({
                    educationId: e.educationId,
                    educationLevel: e.educationLevel || '',
                    enrollmentNumber: e.enrollmentNumber ?? e.enrollment ?? '',
                    enrolledYear: e.enrolledYear || '',
                    passingYear: e.passingYear ?? e.passOutYear ?? '',
                    qualification: e.qualification || '',
                    specialization: e.specialization || '',
                    instituteName: e.instituteName || '',
                    educationProofFileName: basename(e.educationFileUrl || e.educationProofFileName || ''),
                    educationFileUrl: e.educationFileUrl || null,
                    candidateId: e.candidateId || candidateId,
                }));

                setEducationEntries(mapped);
                setErrors(mapped.map(() => ({ file: '' })));
                setOverallRemarks(backendData[0]?.remarks || '');
                setOverallVerificationStatus(false);
            } catch (err) {
                console.error('Error initializing education data:', err);
                setError('Failed to load education info.');
            } finally {
                setLoading(false);
            }
        };

        init();
    }, [candidateId, educationInfos, token]);

    const handleOverallRemarksChange = (e) => setOverallRemarks(e.target.value);

    // const handleFileChange = (e, index) => {
    //     const file = e.target.files?.[0];
    //     if (file) {
    //         setEducationProofFiles(prev => ({ ...prev, [index]: file }));
    //         setEducationEntries(prev =>
    //             prev.map((entry, i) =>
    //                 i === index
    //                     ? { ...entry, educationProofFileName: file.name }
    //                     : entry
    //             )
    //         );
    //         setErrors(prev => prev.map((er, i) => (i === index ? { ...er, file: '' } : er)));
    //         //console.log("Selected file:", file);
    //     }
    // };

   const handleFileChange = (e, index) => {
    setErrors([])
  const file = e.target.files[0];
  if (!file) return;

  const updatedEntries = [...educationEntries];
  updatedEntries[index].file = file;

  // Generate temporary preview URL
  updatedEntries[index].educationFileUrl = URL.createObjectURL(file);

  // Store the actual file name
  updatedEntries[index].educationProofFileName = file.name;

  setEducationEntries(updatedEntries);

  // Keep file for form submission
  setEducationProofFiles(prev => ({
    ...prev,
    [index]: file
  }));
};


   /** Validation Logic */
// Line 77
const validateForm = () => {
    let isValid = true;

    // 1. Remarks are mandatory
    if (!overallRemarks.trim()) {
        toast.error('Overall remarks are required.');
        isValid = false;
    }

    // 2. Each education entry must have at least one proof (uploaded file OR existing API URL)
    const newErrors = educationEntries.map((entry, index) => {
        // FIX: properly check if proof exists
        const hasProof =
            (entry.educationFileUrl && entry.educationFileUrl.trim() !== '') ||
            educationProofFiles[index];

        if (!hasProof) {
            isValid = false;
            return { file: 'Education proof is required for this entry.' };
        }
        return { file: '' };
    });
    setErrors(newErrors);

    if (!isValid && overallRemarks.trim()) {
        // Show general toast only if remarks are OK
        toast.error('Please upload at least one proof for each education entry.');
    }

    return isValid;
};
// Line 95


    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        try {
            if (!token) throw new Error('Authentication token not found. Please log in.');

            const formData = new FormData();
            formData.append('candidateId', String(candidateId));
            formData.append('isVerified', String(overallVerificationStatus));
            formData.append('remarks', overallRemarks);

            // Add the rest of the education data
            const payload = educationEntries.map(({ educationProofFileName, educationFileUrl, ...rest }) => rest);
            formData.append('educationData', JSON.stringify(payload));

            // Include either uploaded file OR already existing API proof
            educationEntries.forEach((entry, idx) => {
                if (educationProofFiles[idx]) {
                    // Newly uploaded file
                    formData.append('files', educationProofFiles[idx]);
                    formData.append('fileEnrollments', entry.educationLevel);
                } else if (entry.educationFileUrl) {
                    // Existing file from API
                    formData.append('existingFiles', entry.educationFileUrl);
                    formData.append('fileEnrollments', entry.educationLevel);
                }
            });

            const response = await fetch('https://trueface.co.in/api/employee/education/verifyBackground', {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: formData,
            });

            if (!response.ok) {
                let msg = 'Failed to submit education info';
                try {
                    const errJson = await response.json();
                    msg = errJson.message || msg;
                } catch {}
                throw new Error(msg);
            }

            await response.json();
            toast.success('Education info submitted successfully!');
            onBack();
        } catch (err) {
            console.error('Submit error:', err);
            setError(err.message);
            toast.error('Error: ' + err.message);
        }
    };

    const handleCancel = () => onBack();

    if (loading) return <div>Loading education info...</div>;
    if (error) return <div className="error-message">{error}</div>;

    return (
        <div className="dashboard-app-container">
            <Sidebar />
            <div className="dashboard-right-panel">
                <TopNavbar />
                <div className="education-info-container">
                    <h2>Education Status</h2>
                    <form onSubmit={handleSubmit}>
                        {educationEntries.map((entry, index) => (
                            <div key={entry.educationId || index} className="education-details-card">
                                <h3>Education Details - {entry.educationLevel}</h3>

                                <div className="form-row">
                                    <div className="emp-form-group">
                                        <label>Enrollment No</label>
                                        <input type="text" value={entry.enrollmentNumber || ''} disabled />
                                    </div>
                                    <div className="emp-form-group">
                                        <label>Year of Enrollment</label>
                                        <input type="text" value={entry.enrolledYear || ''} disabled />
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="emp-form-group">
                                        <label>Year of Passout</label>
                                        <input type="text" value={entry.passingYear || ''} disabled />
                                    </div>
                                    <div className="emp-form-group">
                                        <label>Qualification</label>
                                        <input type="text" value={entry.qualification || ''} disabled />
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="emp-form-group">
                                        <label>Specialization</label>
                                        <input type="text" value={entry.specialization || ''} disabled />
                                    </div>
                                    <div className="emp-form-group full-width">
                                        <label>Institute</label>
                                        <input type="text" value={entry.instituteName || ''} disabled />
                                    </div>
                                </div>

      <div className="form-row">
  <div className="emp-form-group full-width">
    <label>Education Proof</label>

    {/* Always display file as a clickable link */}
    {/* {entry.educationFileUrl ? (
      <a
        href={entry.educationFileUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="file-link"
      >
        {entry.educationFileUrl.split('/').pop()}
      </a>
    ) : (
      <span className="file-name-display">No file selected</span>
    )} */}

    {entry.educationFileUrl ? (
  <a
    href={entry.educationFileUrl}
    target="_blank"
    rel="noopener noreferrer"
    className="file-link"
  >
    {entry.educationProofFileName || entry.educationFileUrl.split('/').pop()}
  </a>
) : (
  <span className="file-name-display">No file selected</span>
)}


    {/* File input */}
    <input
      type="file"
      className="file-upload-btn"
      onChange={(e) => handleFileChange(e, index)}
      accept=".pdf,.jpg,.jpeg,.png"
    />

    {errors[index]?.file && <p className="error-text">{errors[index].file}</p>}
  </div>
</div>




                            </div>
                        ))}

                        <div className="overall-controls-section">
                            <div className="form-row">
                                <div className="emp-form-group full-width">
                                    <label>Overall Remarks</label>
                                    <textarea
                                        name="overallRemarks"
                                        value={overallRemarks}
                                        onChange={handleOverallRemarksChange}
                                        placeholder="Type here..."
                                    ></textarea>
                                </div>
                            </div>

                            <div className="emp-form-group checkbox-group">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={overallVerificationStatus}
                                        onChange={(e) => setOverallVerificationStatus(e.target.checked)}
                                    /> Verification Status
                                </label>
                                <p className="verification-note">
                            ⚠️ Once verified, this step will be locked and you cannot return to modify the education details.
                        </p>
                            </div>
                        </div>

                        <div className="form-actions">
                            <button type="button" onClick={handleCancel} className="cancel-button">Cancel</button>
                            <button type="submit" className="submit-button">Submit</button>
                        </div>
                    </form>
                </div>
            </div>
            {/* Toast notifications */}
            <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} closeOnClick pauseOnHover />
        </div>
    );
};

EducationInfo.propTypes = {
    candidateId: PropTypes.number.isRequired,
    educationInfos: PropTypes.array.isRequired,
    onBack: PropTypes.func.isRequired,
};

export default EducationInfo;